import { useState } from "react";
import { doc, runTransaction, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

const MARKER = "ceylon-mall-demo-v1";

const TYPES = [
  ["Fashion", "Ceylon Fashion", "#a855f7"],
  ["Electronics", "Tech Island", "#2563eb"],
  ["Beauty", "Glow Studio", "#db2777"],
  ["Food", "Spice Market", "#ea580c"],
  ["Home", "Island Living", "#059669"],
  ["General", "Ceylon Gifts", "#0891b2"],
];

const ids = Array.from(
  { length: 30 },
  (_, i) => `mall-demo-${String(i + 1).padStart(2, "0")}`,
);

export default function DemoMallShops({ onChanged }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  async function change(remove) {
    setBusy(true);
    setMessage("");

    try {
      const changed = await runTransaction(db, async (transaction) => {
        const refs = ids.map((id) => doc(db, "stores", id));

        // Complete all reads before writing.
        const snapshots = await Promise.all(
          refs.map((ref) => transaction.get(ref)),
        );

        let total = 0;

        snapshots.forEach((snapshot, index) => {
          const existing = snapshot.data();

          if (remove) {
            // Only delete shops created by this demo tool.
            if (existing?.demoSeed === MARKER) {
              transaction.delete(refs[index]);
              total++;
            }
            return;
          }

          // Keep every existing shop unchanged.
          if (snapshot.exists()) return;

          const [category, name, color] = TYPES[index % TYPES.length];
          const number = String(index + 1).padStart(2, "0");

          transaction.set(refs[index], {
            displayName: `${name} ${number} (Demo)`,
            storeName: `${name} ${number} (Demo)`,
            slug: `ceylon-mall-demo-${number}`,
            instructorId: ids[index],

            category,
            categories: [category],
            brandColor: color,

            description:
              "Demo shop for testing the 3D mall layout and lift. No items are for sale.",

            published: true,
            hasStorePackage: true,
            storePackageStatus: "active",
            adminDisabled: false,
            adminDeleted: false,

            demoSeed: MARKER,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });

          total++;
        });

        return total;
      });

      setMessage(
        remove
          ? `Removed ${changed} demo shops.`
          : `Created ${changed} demo shops. Existing shops were kept.`,
      );

      onChanged?.();
    } catch (error) {
      setMessage(`Demo shop action failed: ${error.message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mt-8 border-t pt-6">
      <h2 className="text-xl font-bold">Demo shops</h2>

      <p className="mt-2 text-gray-600">
        Add 30 published demo shops across six categories to test floors and the
        lift. These shops are visible to everyone and contain no products.
        Repeating the seed action does not create duplicates.
      </p>

      <div className="mt-4 flex flex-wrap gap-3">
        <button
          type="button"
          disabled={busy}
          onClick={() => change(false)}
          className="rounded-lg bg-emerald-600 px-4 py-3 text-white disabled:opacity-50"
        >
          {busy ? "Working…" : "Seed 30 demo shops"}
        </button>

        <button
          type="button"
          disabled={busy}
          onClick={() => change(true)}
          className="rounded-lg border border-red-300 px-4 py-3 text-red-700 disabled:opacity-50"
        >
          Remove demo shops
        </button>
      </div>

      {message && (
        <p role="status" className="mt-3">
          {message}
        </p>
      )}
    </section>
  );
}
